
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, HeartIcon, ShoppingCartIcon, StarIcon } from './icons';
import { useAuth } from '../contexts/AuthContext';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../lib/firebase';

import { WardrobeItem } from '../types';
import HistoryPanel from './HistoryPanel';
import { UserStats, saveUserProfile, TryOnResult } from '../services/db';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
  favoriteProducts: WardrobeItem[];
  onToggleFavorite: (id: string) => void;
  userStats?: UserStats | null;
  isLoadingStats?: boolean;
  onOpenSubscription?: () => void;
  onTestIncrement?: () => void;
  onOpenAdminDashboard?: () => void;
  onGarmentSelect?: (file: File, info: WardrobeItem) => void;
  onHistoryItemSelect?: (item: TryOnResult) => void;
  isLoading?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({
  isOpen,
  onClose,
  favoriteProducts,
  onToggleFavorite,
  userStats,
  isLoadingStats,
  onOpenSubscription,
  onTestIncrement,
  onOpenAdminDashboard,
  onGarmentSelect,
  onHistoryItemSelect,
  isLoading
}) => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'history' | 'favorites' | 'settings'>('profile');
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Clear inputs when closing or switching modes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setName('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isLoginMode) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await saveUserProfile({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName
        });
        if (!userCredential.user.emailVerified) {
          // If not verified, sign them out immediately and show error
          await signOut();
          setError('Please verify your account via the link sent to your email before logging in.');
          return;
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, {
          displayName: name
        });
        await sendEmailVerification(userCredential.user);
        await saveUserProfile({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: name
        });
        setSuccess('Account created successfully! Please check your email to verify your account.');
        setIsLoginMode(true); // Switch to login after signup
      }
      // Close modal on success if desired, or just show profile
    } catch (err: any) {
      console.error(err);
      let msg = 'An unexpected error occurred';
      if (err.code === 'auth/invalid-email') msg = 'Invalid email address';
      if (err.code === 'auth/user-disabled') msg = 'This account has been disabled';
      if (err.code === 'auth/user-not-found') msg = 'User not found';
      if (err.code === 'auth/wrong-password') msg = 'Incorrect password';
      if (err.code === 'auth/email-already-in-use') msg = 'Email is already in use';
      if (err.code === 'auth/weak-password') msg = 'Password is too weak (must be at least 6 characters)';

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await saveUserProfile({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });
    } catch (err: any) {
      console.error(err);
      let msg = 'Google sign-in failed';
      if (err.code === 'auth/popup-blocked') msg = 'Popup blocked, please allow popups in your browser';
      if (err.code === 'auth/popup-closed-by-user') msg = 'Sign-in popup closed by user';
      if (err.code === 'auth/operation-not-allowed') msg = 'Google sign-in must be enabled in Firebase console';
      setError(msg + (err.code ? ` (${err.code})` : ''));
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: '👤' },
    { id: 'history', name: 'History', icon: '🕘' },
    { id: 'favorites', name: 'Favorites', icon: '❤️' },
    { id: 'settings', name: 'Settings', icon: '⚙️' }
  ];

  const mockOrders = [
    {
      id: 'ORD-001',
      date: '2024-01-15',
      status: 'تم التسليم',
      total: 450,
      items: ['Classic Black Abaya', 'Elegant Silk Hijab']
    },
    {
      id: 'ORD-002',
      date: '2024-01-10',
      status: 'Shipping',
      total: 720,
      items: ['Gulf Evening Dress']
    }
  ];


  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                {user ? 'My Profile' : (isLoginMode ? 'Login' : 'Create Account')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>

            {user && (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{user.displayName || 'User'}</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-600">{user.email}</p>
                    {user.emailVerified ? (
                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Verified</span>
                    ) : (
                      <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Not Verified</span>
                    )}
                  </div>

                  {/* Credits Display */}
                  {isLoadingStats ? (
                    <div className="mt-3 p-3 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
                      <span className="text-sm text-purple-600">Loading credits...</span>
                    </div>
                  ) : userStats && (
                    <div className="mt-3 p-3 bg-purple-50 rounded-xl border border-purple-100 flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-[10px] text-purple-600 font-bold uppercase tracking-wider">Remaining Credits</p>
                          <p className="text-lg font-bold text-purple-900 leading-none">
                            {Math.max(0, userStats.allowedTryOnCount - userStats.tryOnCount)} <span className="text-sm font-normal text-purple-600">tries</span>
                          </p>
                        </div>
                        <button
                          onClick={onOpenSubscription}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-purple-700 transition-all active:scale-95"
                        >
                          Top Up ✨
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!user ? (
            <div className="p-6 flex-1 overflow-y-auto">
              <form onSubmit={handleAuth} className="space-y-4">
                {!isLoginMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                {error && <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg border border-red-100">{error}</p>}
                {success && <p className="text-green-600 text-sm font-medium bg-green-50 p-3 rounded-lg border border-green-100">{success}</p>}

                <button
                  disabled={loading}
                  type="submit"
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : (isLoginMode ? 'Login' : 'Sign Up')}
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">أو</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                  Sign in with Google
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setIsLoginMode(!isLoginMode)}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                >
                  {isLoginMode ? "Don't have an account? Sign up" : 'Already have an account? Login'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex-1 py-4 px-2 text-center font-medium transition-colors ${activeTab === tab.id
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <span className="block text-lg mb-1">{tab.icon}</span>
                    <span className="text-sm">{tab.name}</span>
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {activeTab === 'profile' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            disabled
                            value={user.email || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                          />
                        </div>
                        {!user.emailVerified && (
                          <div className="bg-yellow-50 border border-yellow-100 p-4 rounded-lg">
                            <p className="text-sm text-yellow-800 mb-2">
                              Your account is not verified yet. Please check your email.
                            </p>
                            <button
                              onClick={async () => {
                                try {
                                  await sendEmailVerification(user);
                                  setSuccess('Verification link sent successfully.');
                                } catch (err) {
                                  setError('Failed to send link, please try again later.');
                                }
                              }}
                              className="text-sm text-yellow-900 underline font-medium hover:text-yellow-950"
                            >
                              Resend Verification Link
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div className="space-y-4">
                    <HistoryPanel
                      onGarmentSelect={(file, info) => {
                        if (onGarmentSelect) onGarmentSelect(file, info);
                        onClose(); // Close modal when item is selected
                      }}
                      onHistoryItemSelect={(item) => {
                        if (onHistoryItemSelect) onHistoryItemSelect(item);
                        onClose();
                      }}
                      isLoading={isLoading || false}
                    />
                  </div>
                )}

                {activeTab === 'favorites' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold mb-4">Favorites</h3>
                    {favoriteProducts.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No favorites yet.</p>
                    ) : (
                      favoriteProducts.map((item) => (
                        <div key={item.id} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                          <img
                            src={item.url}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">{item.name}</h4>
                            <p className="text-lg font-bold text-purple-600">{item.price} ر.س</p>
                          </div>
                          <button
                            onClick={() => onToggleFavorite(item.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <HeartIcon className="w-5 h-5 fill-current" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold mb-4">Settings</h3>



                    <button
                      onClick={() => signOut()}
                      className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserProfile;
